import { type HotelPhoto } from '@utazzo/shared';
import { X } from 'lucide-react';
import React from 'react';

import { MediaDropField } from '@/features/website-cms/components/MediaDropField';
import { MediaGalleryField } from '@/features/website-cms/components/MediaGalleryField';

/**
 * Hotel photos: cover (single banner) + gallery (multi, draggable). Uploads go
 * straight to S3 via the presigned flow; the stored value is the media-library
 * id, never a raw URL. The gallery reuses MediaGalleryField, which supports
 * selecting several images at once and drag-to-reorder. Legacy rows that
 * imported plain URLs render as read-only thumbnails the operator can remove.
 *
 * photos[0] (sortOrder 0) is the cover; the rest are the gallery in order.
 */

interface HotelPhotosCardValue {
  photos: HotelPhoto[];
  onChange: (next: HotelPhoto[]) => void;
  disabled?: boolean;
}

const UPLOAD_FOLDER = 'hotels';

export const HotelPhotosFields = ({
  photos,
  onChange,
  disabled,
}: HotelPhotosCardValue): React.JSX.Element => {
  const cover = photos.find((p) => (p.sortOrder ?? 0) === 0) ?? null;
  const galleryPhotos = photos
    .filter((p) => (p.sortOrder ?? 0) !== 0)
    .sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0));
  const legacyUrlPhotos = galleryPhotos.filter((p) => p.url && !p.mediaId);
  const galleryIds = galleryPhotos
    .filter((p): p is HotelPhoto & { mediaId: string } => Boolean(p.mediaId))
    .map((p) => p.mediaId);

  /** Re-number: cover at 0, then gallery 1..n. */
  const rebuild = (nextCover: HotelPhoto | null, nextGallery: HotelPhoto[]): void => {
    const next: HotelPhoto[] = [];
    if (nextCover) next.push({ ...nextCover, sortOrder: 0 });
    nextGallery.forEach((p, i) => next.push({ ...p, sortOrder: i + 1 }));
    onChange(next);
  };

  const setCover = (mediaId: string | null): void => {
    rebuild(mediaId ? { mediaId } : null, galleryPhotos);
  };

  // Media-id gallery items are managed (multi-add + reorder) by MediaGalleryField;
  // legacy URL items are preserved at the end.
  const onGalleryIdsChange = (ids: string[]): void => {
    rebuild(cover, [...ids.map((mediaId) => ({ mediaId })), ...legacyUrlPhotos]);
  };

  const removeLegacyUrl = (photo: HotelPhoto): void => {
    if (cover === photo) {
      rebuild(null, galleryPhotos);
      return;
    }
    rebuild(
      cover,
      galleryPhotos.filter((p) => p !== photo),
    );
  };

  return (
    <div className="space-y-5">
      {/* Cover */}
      {cover?.url && !cover.mediaId ? (
        <LegacyUrlThumb
          photo={cover}
          label="Cover photo (legacy URL)"
          onRemove={() => removeLegacyUrl(cover)}
          disabled={disabled}
        />
      ) : (
        <MediaDropField
          label="Cover photo"
          value={cover?.mediaId ?? null}
          onChange={setCover}
          folder={UPLOAD_FOLDER}
          disabled={disabled}
          variant="banner"
        />
      )}

      {/* Gallery - multi-select + drag-to-reorder */}
      <MediaGalleryField
        label="Gallery"
        value={galleryIds}
        onChange={onGalleryIdsChange}
        folder={UPLOAD_FOLDER}
        disabled={disabled}
      />

      {/* Legacy URL gallery photos (read-only, removable) */}
      {legacyUrlPhotos.length > 0 && (
        <div className="space-y-1.5">
          <span className="text-xs font-medium text-zinc-500">Imported photos (legacy URLs)</span>
          <div className="grid grid-cols-3 gap-3 sm:grid-cols-4">
            {legacyUrlPhotos.map((photo, i) => (
              <LegacyUrlThumb
                key={`url-${photo.url}-${i}`}
                photo={photo}
                onRemove={() => removeLegacyUrl(photo)}
                disabled={disabled}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

/** Read-only thumbnail for legacy URL-based photos. */
const LegacyUrlThumb = ({
  photo,
  label,
  onRemove,
  disabled,
}: {
  photo: HotelPhoto;
  label?: string;
  onRemove: () => void;
  disabled?: boolean;
}): React.JSX.Element => (
  <div className="space-y-1.5">
    {label && <span className="text-sm leading-none font-medium text-zinc-900">{label}</span>}
    <div className="relative aspect-square overflow-hidden rounded-md border border-zinc-200">
      <img src={photo.url} alt={photo.alt ?? ''} className="h-full w-full object-cover" />
      <button
        type="button"
        onClick={onRemove}
        disabled={disabled}
        aria-label="Remove photo"
        className="absolute top-1 right-1 rounded-full bg-white/90 p-1 text-zinc-600 shadow-sm hover:text-red-600"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  </div>
);
